import { useAtom, useSetAtom } from "jotai";
import { useTranslation } from "react-i18next";

import { layoutAtom, layoutPickerOpenAtom } from "@/core/state";
import { Carte, Grille, Infobulle, Modal, ModalBody, ModalFooter } from "@/shared/ui";
import {
  applyTemplatePreservingPanels,
  LAYOUT_TEMPLATES,
  type LayoutTemplate,
} from "@/shell/workspaces/LayoutTemplates";

/**
 * Modal "choisis une disposition" — style Windows Snap Layouts.
 *
 * Trigger :
 *   - Cmd/Ctrl + Shift + P
 *   - Bouton "+ Ajouter un panneau" dans la TopBar
 *
 * UX :
 *   - 12 thumbnails visuels. Click → applique le layout au workspace actif
 *     (replace), en préservant les panneaux déjà ouverts.
 *   - Escape ou click-outside ferme (c'est le `Modal`).
 *   - L'utilisateur peut ensuite drag-dock ou utiliser le burger menu de
 *     chaque panel pour personnaliser.
 *
 * Il y a un autre picker (PanelPicker) — accessible via le burger menu
 * "Ajouter un onglet ici" — pour ajouter un panel TYPE specifique (Stats,
 * Conversations, etc.) dans un group existant. Le LayoutPickerModal sert a
 * choisir une SHAPE de workspace, le PanelPicker sert a choisir un CONTENU.
 *
 * Dessin (coque, 01-11-2026) : un `Modal` de la librairie, chaque disposition
 * est une `Carte` cliquable dans une `Grille` — plus de fenêtre faite à la main
 * ni de contour gris.
 */
export function LayoutPickerModal() {
  const { t } = useTranslation();
  const [open, setOpen] = useAtom(layoutPickerOpenAtom);
  const setLayout = useSetAtom(layoutAtom);

  function applyTemplate(tpl: LayoutTemplate) {
    // Preserve les panneaux actifs (redistribution dans la nouvelle forme)
    // au lieu de remplir toutes les cellules de placeholders.
    setLayout((prev) => applyTemplatePreservingPanels(prev, tpl));
    setOpen(false);
  }

  return (
    <Modal
      isOpen={open}
      onClose={() => setOpen(false)}
      size="lg"
      ariaLabel={t("shell.layoutPicker.ariaLabel")}
      title={t("shell.layoutPicker.title")}
      subtitle={t("shell.layoutPicker.subtitle")}
      closeLabel={t("common.close")}
    >
      <ModalBody padding="px-4 py-4">
        <Grille min={120} ecart={12}>
          {LAYOUT_TEMPLATES.map((tpl) => {
            const name = t(`shell.workspaces.templates.${tpl.id}`, { defaultValue: tpl.name });
            const cellsLabel = tpl.cells > 1
              ? t("shell.layoutPicker.cellsOther", { count: tpl.cells })
              : t("shell.layoutPicker.cellsOne", { count: tpl.cells });
            return (
              <Infobulle key={tpl.id} texte={`${name} — ${cellsLabel}`}>
                <Carte
                  remplissage="sm"
                  onClick={() => applyTemplate(tpl)}
                  aria-label={`${name} — ${cellsLabel}`}
                  className="flex flex-col gap-2"
                >
                  <div className="aspect-[4/3]">{tpl.thumbnail}</div>
                  <span className="truncate text-center text-[13px] text-muted">{name}</span>
                </Carte>
              </Infobulle>
            );
          })}
        </Grille>
      </ModalBody>

      <ModalFooter className="flex-wrap justify-between text-[12px] text-faint">
        <span>{t("shell.layoutPicker.footerHint")}</span>
        <span className="italic">{t("shell.layoutPicker.footerNote")}</span>
      </ModalFooter>
    </Modal>
  );
}
